import {
  useGetDataCenterTablesInfinite,
  useGetTableDetailsByTableName,
  type Table,
  type TableColumn,
} from "@/queries/data-center/data-center.api";
import { useDebounceHook } from "@/hooks/use-debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Input,
  inputVariants,
  ScrollArea,
} from "@elaniintc/goes-design-system";
import { cn } from "@/utils/theme";
import { DropdownListSeparator } from "@/components/ui/dropdown-list/dropdown-list-separator";

const TABLE_SUCCESS_ICON = {
  type: "far",
  name: "table",
  className: "text-success-quiet",
};
const TABLE_NEUTRAL_ICON = {
  type: "far",
  name: "table",
  className: "text-body",
};
const COLUMN_ICON = {
  type: "far",
  name: "columns",
  className: "text-body",
};

const getNoResultsMessage = (
  searchString: string,
  isFetchingNextPage: boolean,
  isColumnView = false,
) => {
  if (isColumnView) {
    if (searchString.trim().length > 0) {
      return "No se encontraron columnas";
    }
    return "No hay columnas disponibles en esta tabla";
  }
  if (searchString.trim().length > 0) {
    return "No se encontraron tablas";
  }
  if (!isFetchingNextPage) {
    return "No hay tablas disponibles";
  }
  return "";
};

interface RootViewComponentProps {
  filteredTables: Table[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  searchString: string;
  handleRootItemClick: (table: Table) => void;
}

const RootViewComponent = ({
  filteredTables,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  searchString,
  handleRootItemClick,
}: RootViewComponentProps) => {
  return (
    <ScrollArea className="h-66">
      <div className="w-full max-w-(--radix-dropdown-menu-trigger-width)">
        {filteredTables && filteredTables.length > 0 ? (
          filteredTables.map((table) => (
            <button
              key={table.id}
              type="button"
              className="flex w-full items-center gap-2 rounded-lg text-body px-3 py-2 hover:bg-neutral-object-accent-quiet cursor-pointer"
              onClick={() => handleRootItemClick(table)}
            >
              <Icon {...TABLE_NEUTRAL_ICON} className="shrink-0" />
              <div className="flex-auto min-w-0">
                <span className="text-body text-start text-base font-normal truncate block">
                  {table.name}
                </span>
              </div>
              <Badge
                size="sm"
                className="rounded-lg shrink-0"
                shouldHover={false}
                variant={table.deleted_at ? "neutral" : "success"}
              >
                {table.deleted_at ? "Inactivo" : "Activo"}
              </Badge>
              <Icon
                type="fas"
                name="chevron-right"
                className="text-body shrink-0"
              />
            </button>
          ))
        ) : (
          <p className="text-body-quiet text-base font-normal flex-auto py-6 text-center">
            {getNoResultsMessage(searchString, isFetchingNextPage)}
          </p>
        )}
        {hasNextPage && (
          <div className="w-full p-2">
            <Button
              variant="ghost-blue"
              size="sm"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Cargando..." : "Cargar más"}
              <Icon
                type="far"
                name={isFetchingNextPage ? "spinner-third" : "plus"}
                className={isFetchingNextPage ? "animate-spin" : ""}
              />
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

interface SubMenuViewComponentProps {
  internalSelectedTable: Table | null;
  internalFilteredColumns: TableColumn[] | null;
  searchString: string;
  isFetchingNextPage: boolean;
  handleSubmenuItemClick: (column: TableColumn) => void;
  goBackToRoot: () => void;
}
const SubMenuViewComponent = ({
  internalSelectedTable,
  internalFilteredColumns,
  searchString,
  isFetchingNextPage,
  handleSubmenuItemClick,
  goBackToRoot,
}: SubMenuViewComponentProps) => {
  return (
    <ScrollArea className="h-60">
      <div className="w-full max-h-75 p-2 box-border max-w-(--radix-dropdown-menu-trigger-width)">
        {internalSelectedTable && (
          <>
            <button
              type="button"
              className="cursor-pointer"
              aria-label="Volver a lista principal"
              onClick={goBackToRoot}
            >
              <DropdownListSeparator
                options={[internalSelectedTable.name ?? ""]}
                icon={
                  internalSelectedTable.deleted_at
                    ? TABLE_NEUTRAL_ICON
                    : TABLE_SUCCESS_ICON
                }
              />
            </button>
            {internalFilteredColumns && internalFilteredColumns.length > 0 ? (
              internalFilteredColumns.map((column) => (
                <button
                  key={column.id}
                  type="button"
                  className="w-full gap-2 flex justify-between items-center rounded-lg text-body px-3 py-2 hover:bg-neutral-object-accent-quiet cursor-pointer"
                  onClick={() => handleSubmenuItemClick(column)}
                >
                  <Icon {...COLUMN_ICON} />
                  <p className="text-body text-start text-base font-normal flex-auto">
                    {column.name}
                  </p>
                </button>
              ))
            ) : (
              <p className="text-body-quiet text-base font-normal flex-auto py-6 text-center">
                {getNoResultsMessage(searchString, isFetchingNextPage, true)}
              </p>
            )}
          </>
        )}
      </div>
    </ScrollArea>
  );
};

interface DataCenterFieldProps {
  widthClassName?: string;
  fieldKey: string;
  selectedTable: string | null;
  selectedColumn: string | null;
  required?: boolean;
  label?: string;
  labelHelperText?: string;
  helperText?: string;
  placeholder?: string;
  disableSearch?: boolean;
  onChange: (fieldKey: string, tableName: string, columnName: string) => void;
}

export const DataCenterField = ({
  fieldKey,
  selectedTable,
  selectedColumn,
  required = false,
  helperText,
  labelHelperText,
  label = "Seleccione una variable",
  placeholder = "Elige una variable",
  disableSearch = false,
  widthClassName,
  onChange,
}: DataCenterFieldProps) => {
  const [open, setOpen] = useState(false);
  const [fetchSingleElement, setFetchSingleElement] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [internalSelectedTable, setInternalSelectedTable] =
    useState<Table | null>(null);
  const [internalselectedColumn, setInternalselectedColumn] =
    useState<TableColumn | null>(null);

  // Track if user is navigating internally vs syncing from props
  const isInternalNavigationRef = useRef(false);

  // Debounce for API only
  const debouncedTableSearch = useDebounceHook(
    internalSelectedTable ? "" : searchString,
    300,
  );
  const {
    data: tablesData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetDataCenterTablesInfinite(debouncedTableSearch);
  const { data: tableDetailsData } = useGetTableDetailsByTableName(
    selectedTable ?? "",
    undefined,
    fetchSingleElement,
  );
  // columns filtering
  const internalFilteredColumns = useMemo(() => {
    if (!internalSelectedTable?.columns) return [];
    if (!searchString) return internalSelectedTable.columns;
    const search = searchString.toLowerCase();
    return internalSelectedTable.columns.filter((column) =>
      column.name.toLowerCase().includes(search),
    );
  }, [internalSelectedTable, searchString]);

  // Tables come filtered from the API when there is debouncedTableSearch
  const filteredTables = useMemo(() => {
    const tables = tablesData?.pages.flatMap((page) => page.data) || [];
    // If we fetched a single table detail, include it in the list and remove duplicates
    if (tableDetailsData?.data) {
      const singleTable = tableDetailsData.data as unknown as Table;
      const allTables = [singleTable, ...tables];
      // Remove duplicates by id
      const uniqueTables = allTables.filter(
        (table, index, self) =>
          index === self.findIndex((t) => t.id === table.id),
      );
      return uniqueTables;
    }
    return tables;
  }, [tablesData, tableDetailsData]);

  /**
   * Buscar selected table y columna en la lista de tablas filtradas
   * y setear los estados internos
   */
  useEffect(() => {
    // Don't sync if user is navigating internally AND dropdown
    if (isInternalNavigationRef.current) return;

    if (selectedTable) {
      // Build the combined list of tables (same logic as filteredTables) but without the dependency
      const tables = tablesData?.pages.flatMap((page) => page.data) || [];
      let allTables = tables;

      if (tableDetailsData?.data) {
        const singleTable = tableDetailsData.data as unknown as Table;
        allTables = [singleTable, ...tables];
        // Remove duplicates by id
        allTables = allTables.filter(
          (table, index, self) =>
            index === self.findIndex((t) => t.id === table.id),
        );
      }

      // Find the table by name
      const table = allTables.find((t) => t.name === selectedTable) || null;
      if (table) {
        setInternalSelectedTable(table);
        setFetchSingleElement(false);
        if (selectedColumn) {
          const column =
            table.columns?.find(
              (c) => c.name === selectedColumn || c.id === selectedColumn,
            ) || null;
          setInternalselectedColumn(column);
        } else {
          setInternalselectedColumn(null);
        }
      } else {
        // If the table is not found in the current list, fetch its details
        setFetchSingleElement(true);
      }
    } else {
      setInternalSelectedTable(null);
      setInternalselectedColumn(null);
      setFetchSingleElement(false);
    }

    // Reset the flag when dropdown is closed
    if (!open) {
      isInternalNavigationRef.current = false;
    }
  }, [
    selectedTable,
    selectedColumn,
    tableDetailsData,
    tablesData?.pages,
    open,
  ]);

  const handleRootItemClick = (table: Table) => {
    isInternalNavigationRef.current = true;
    setInternalSelectedTable(table);
    setInternalselectedColumn(null);
    setSearchString("");
  };

  /**
   * Handles the click event for a submenu item (column).
   * send the data to onChange prop
   * @param column
   */
  const handleSubmenuItemClick = (column: TableColumn) => {
    setInternalselectedColumn(column);
    setSearchString("");
    onChange(fieldKey, internalSelectedTable!.name, column.name);
    setOpen(false);
  };

  const goBackToRoot = () => {
    isInternalNavigationRef.current = true;
    setInternalSelectedTable(null);
    setInternalselectedColumn(null);
    setSearchString("");
  };

  const TriggerButton = (
    <Button
      group="single"
      variant="tertiary"
      className={cn(
        "flex justify-start items-center group min-w-0 relative gap-2",
        inputVariants({
          className: cn(
            "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
            widthClassName,
          ),
        }),
      )}
      aria-label={label}
    >
      <Badge
        shape="pill"
        size="sm"
        variant={
          internalSelectedTable && !internalSelectedTable.deleted_at
            ? "success"
            : "neutral"
        }
        className={cn(
          "min-w-6 px-1 flex justify-center items-center text-body-quiet",
        )}
      >
        <Icon
          {...(internalSelectedTable && !internalSelectedTable.deleted_at
            ? TABLE_SUCCESS_ICON
            : TABLE_NEUTRAL_ICON)}
        />
      </Badge>
      <Icon {...COLUMN_ICON} />
      <p
        className={cn(
          "truncate font-normal pr-5",
          internalselectedColumn ? "text-body" : "text-body-quiet",
        )}
      >
        {internalselectedColumn ? internalselectedColumn.name : placeholder}
      </p>

      <Icon
        type="far"
        name="angle-down"
        className="group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body absolute right-3 top-1/2 transform -translate-y-1/2"
      />
    </Button>
  );

  const SearchBox = disableSearch ? null : (
    <div className="border-b border-neutral-quiet p-3 relative ">
      <Icon
        type="far"
        name="search"
        className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
      />
      <Input
        variant="sm"
        className="text-sm w-full font-normal flex-auto pl-9"
        placeholder={"Buscar..."}
        onChange={(e) => setSearchString(e.target.value)}
        value={searchString}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex w-full justify-between items-center">
        <label className="font-semibold text-body-loud text-sm leading-2xl">
          {label}
          {required && <span className="text-sm text-danger ml-1">*</span>}
        </label>
        {labelHelperText && (
          <p className="text-body-quiet text-sm">{labelHelperText}</p>
        )}
      </div>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>{TriggerButton}</DropdownMenuTrigger>
        <DropdownMenuContent
          className={cn(
            "flex flex-col rounded-lg border-[0.5px] p-0 border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl",
            "max-w-(--radix-dropdown-menu-trigger-width) w-(--radix-dropdown-menu-trigger-width)",
            widthClassName,
          )}
          side="bottom"
          align="start"
        >
          {SearchBox}
          {internalSelectedTable ? (
            <SubMenuViewComponent
              goBackToRoot={goBackToRoot}
              handleSubmenuItemClick={handleSubmenuItemClick}
              internalFilteredColumns={internalFilteredColumns}
              internalSelectedTable={internalSelectedTable}
              isFetchingNextPage={isFetchingNextPage}
              searchString={searchString}
            />
          ) : (
            <RootViewComponent
              fetchNextPage={fetchNextPage}
              filteredTables={filteredTables}
              handleRootItemClick={handleRootItemClick}
              hasNextPage={!!hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              searchString={searchString}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {helperText && (
        <p className="text-xs text-body-quiet mt-1">{helperText}</p>
      )}
    </div>
  );
};
